import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Calendar, CheckCircle, TrendingUp, Loader, Clock, Paperclip } from 'lucide-react';
import { subscribeToProjectMilestones, createMilestone, updateMilestone, deleteMilestone } from '../../services/milestoneService';
import { getProjectById, updateProject, calculateProjectStats, getAllProjects } from '../../services/projectService';
import { getProjectFiles } from '../../services/storageService';
import { 
  notifyProjectCompleted, 
  notifyProjectReopened,
  notifyMilestoneCreated,
  notifyMilestoneStatusChanged,
  notifyMilestoneUpdated,
  notifyMilestoneDeleted
} from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import MilestoneCard from '../common/MilestoneCard';
import MilestoneForm from './MilestoneForm';
import FilesModal from '../common/FilesModal';
import './ProjectDetail.css';

const ProjectDetail = ({ projectId, onBack }) => {
  const { currentUser } = useAuth();
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [stats, setStats] = useState({ totalMilestones: 0, completedMilestones: 0, progress: 0, isComplete: false });
  const [projectFiles, setProjectFiles] = useState([]);
  const [filesModal, setFilesModal] = useState(null); // { title, files }

  useEffect(() => {
    loadProject();
    loadFiles();

    const unsubscribe = subscribeToProjectMilestones(projectId, (data) => {
      const sorted = data.sort((a, b) => {
        if (a.urgent && !b.urgent) return -1;
        if (!a.urgent && b.urgent) return 1;
        return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      });
      setMilestones(sorted);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (project && milestones) {
      setStats(calculateProjectStats(project, milestones));
    }
  }, [project, milestones]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const projectData = await getProjectById(projectId);
      setProject(projectData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading project:', error);
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      const files = await getProjectFiles(projectId);
      setProjectFiles(files);
    } catch (error) {
      console.error('Error loading files:', error);
    }
  };

  // Get files for a specific milestone
  const getMilestoneFiles = (milestoneId) =>
    projectFiles.filter(f => f.milestoneId === milestoneId);

  // Get project-level files (no milestone)
  const getUnassignedFiles = () =>
    projectFiles.filter(f => !f.milestoneId);

  const handleViewFiles = (milestoneId, milestoneTitle) => {
    setFilesModal({
      title: `Files — ${milestoneTitle}`,
      files: getMilestoneFiles(milestoneId)
    });
  };

  const handleViewProjectFiles = () => {
    setFilesModal({
      title: `Project Files — ${project.name}`,
      files: getUnassignedFiles()
    });
  };

  const handleCreateMilestone = async (milestoneData) => {
    if (!project || !project.clientId) {
      alert('Project data not loaded. Please try again.');
      return;
    }

    const result = await createMilestone({
      ...milestoneData,
      projectId,
      clientId: project.clientId,
      clientName: project.clientName
    });

    if (result.success) {
      setShowMilestoneForm(false);
      await notifyMilestoneCreated(
        project.clientId,
        milestoneData.title,
        project.name,
        currentUser.displayName || 'Admin'
      );
    } else {
      alert('Error creating milestone: ' + result.error);
    }
  };

  const handleUpdateMilestone = async (milestoneData) => {
    const result = await updateMilestone(editingMilestone.id, milestoneData);
    if (result.success) {
      setShowMilestoneForm(false);
      setEditingMilestone(null);
      await notifyMilestoneUpdated(
        project.clientId,
        milestoneData.title || editingMilestone.title,
        currentUser.displayName || 'Admin'
      );
    } else {
      alert('Error updating milestone: ' + result.error);
    }
  };

  const handleEdit = (milestone) => {
    setEditingMilestone(milestone);
    setShowMilestoneForm(true);
  };

  const handleDelete = async (milestoneId) => {
    const milestone = milestones.find(m => m.id === milestoneId);
    if (!milestone) return;
    if (!window.confirm('Are you sure you want to delete this milestone?')) return;

    const result = await deleteMilestone(milestoneId);
    if (!result.success) {
      alert('Error deleting milestone: ' + result.error);
    } else {
      await notifyMilestoneDeleted(
        project.clientId,
        milestone.title,
        currentUser.displayName || 'Admin'
      );
      loadFiles(); // refresh files after deletion
    }
  };

  const handleUpdateStatus = async (milestoneId, newStatus) => {
    const milestone = milestones.find(m => m.id === milestoneId);
    if (!milestone) return;
    const oldStatus = milestone.status;

    const confirmMsg = newStatus === 'completed'
      ? 'Mark this milestone as completed?'
      : 'Are you sure you want to reopen this milestone?';
    if (!window.confirm(confirmMsg)) return;

    const result = await updateMilestone(milestoneId, { status: newStatus });
    if (!result.success) {
      alert('Error updating status: ' + result.error);
    } else {
      await notifyMilestoneStatusChanged(
        project.clientId,
        milestone.title,
        oldStatus,
        newStatus,
        currentUser.displayName || 'Admin'
      );
    }
  };

  const handleCompleteProject = async () => {
    if (!window.confirm('Mark this project as completed? This will notify the client for payment.')) return;

    const result = await updateProject(projectId, { status: 'completed' });
    if (result.success) {
      loadProject();
      await notifyProjectCompleted(project.clientId, project.name, currentUser.displayName || 'Admin');
    } else {
      alert('Error completing project: ' + result.error);
    }
  };

  const handleReopenProject = async () => {
    try {
      const allProjects = await getAllProjects();
      const hasActive = allProjects.some(p =>
        p.clientId === project.clientId && p.status === 'active' && p.id !== projectId
      );
      if (hasActive) {
        alert('This client already has an active project.');
        return;
      }
    } catch (error) {
      console.error('Error checking active projects:', error);
    }

    if (!window.confirm('Reopen this project?')) return;

    const result = await updateProject(projectId, { status: 'active', completedAt: null });
    if (result.success) {
      loadProject();
      await notifyProjectReopened(project.clientId, project.name, currentUser.displayName || 'Admin');
    } else {
      alert('Error reopening project: ' + result.error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return timestamp.toDate().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="project-detail-loading">
        <Loader className="spinner" size={32} />
        <p>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-error">
        <p>Project not found</p>
        <button onClick={onBack} className="btn-back">Go Back</button>
      </div>
    );
  }

  const initialFormData = editingMilestone || (project ? {
    clientId: project.clientId,
    clientName: project.clientName,
    projectId
  } : {});

  const unassignedFiles = getUnassignedFiles();

  const renderMilestoneGroup = (filteredMilestones) =>
    filteredMilestones.map(milestone => (
      <MilestoneCard
        key={milestone.id}
        milestone={milestone}
        onEdit={project.status === 'active' ? handleEdit : null}
        onDelete={project.status === 'active' ? handleDelete : null}
        onUpdateStatus={project.status === 'active' ? handleUpdateStatus : null}
        isAdmin={true}
        files={getMilestoneFiles(milestone.id)}
        onViewFiles={handleViewFiles}
      />
    ));

  return (
    <div className="project-detail">
      <div className="project-detail-header">
        <button onClick={onBack} className="btn-back">
          <ArrowLeft size={20} />
          Back to Projects
        </button>
      </div>

      <div className="project-info-card">
        <div className="project-info-header">
          <div>
            <h1>{project.name}</h1>
            <p className="project-client">{project.clientName}</p>
          </div>
          <span className={`project-status-badge ${project.status}`}>
            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
          </span>
        </div>

        {project.description && (
          <p className="project-description">{project.description}</p>
        )}

        <div className="project-metadata">
          {project.budget && (
            <div className="metadata-item">
              <span>{formatCurrency(project.budget)}</span>
            </div>
          )}
          {project.startDate && (
            <div className="metadata-item">
              <Calendar size={16} />
              <span>Started: {formatDate(project.startDate)}</span>
            </div>
          )}
          {project.endDate && (
            <div className="metadata-item">
              <Calendar size={16} />
              <span>Est. End: {formatDate(project.endDate)}</span>
            </div>
          )}
        </div>

        <div className="project-progress-section">
          <div className="progress-header">
            <div className="progress-info">
              <TrendingUp size={20} />
              <span>Progress</span>
            </div>
            <span className="progress-percentage">{stats.progress}%</span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${stats.progress}%` }} />
          </div>
          <p className="progress-text">
            {stats.completedMilestones} of {stats.totalMilestones} milestones completed
          </p>
        </div>

        {milestones.length > 0 && milestones.every(m => m.status === 'completed') && project.status === 'active' && (
          <div className="project-actions">
            <button onClick={handleCompleteProject} className="btn-complete-project">
              <CheckCircle size={18} />
              Mark Project as Completed
            </button>
          </div>
        )}

        {project.status === 'completed' && (
          <div className="project-actions">
            <button onClick={handleReopenProject} className="btn-reopen-project">
              <Clock size={18} />
              Reopen Project
            </button>
          </div>
        )}
      </div>

      {/* Project-level files (no milestone) */}
      {unassignedFiles.length > 0 && (
        <div className="project-files-bar">
          <span>Files not assigned to a milestone ({unassignedFiles.length})</span>
          <button className="btn-view-project-files" onClick={handleViewProjectFiles}>
            <Paperclip size={16} />
            View Files ({unassignedFiles.length})
          </button>
        </div>
      )}

      <div className="milestones-section">
        <div className="milestones-header">
          <h2>Milestones ({milestones.length})</h2>
          {project.status === 'active' && (
            <button onClick={() => setShowMilestoneForm(true)} className="btn-add-milestone">
              <Plus size={18} />
              Add Milestone
            </button>
          )}
        </div>

        {project.status !== 'active' && (
          <div className="project-locked-notice">
            <p>
              This project is <strong>{project.status}</strong>.
              {project.status === 'completed' && ' Reopen the project to edit milestones.'}
              {project.status === 'paid' && ' This project is archived.'}
            </p>
          </div>
        )}

        <div className="milestones-list">
          {milestones.length === 0 ? (
            <div className="milestones-empty">
              <p>No milestones yet</p>
              {project.status === 'active' && (
                <button onClick={() => setShowMilestoneForm(true)} className="btn-add-first">
                  <Plus size={18} />
                  Create First Milestone
                </button>
              )}
            </div>
          ) : (
            <>
              {milestones.filter(m => m.status === 'in_progress').length > 0 && (
                <div className="milestone-group">
                  <h3 className="group-title in-progress">
                    <Clock size={18} />
                    Milestones In Progress ({milestones.filter(m => m.status === 'in_progress').length})
                  </h3>
                  <div className="group-list">
                    {renderMilestoneGroup(milestones.filter(m => m.status === 'in_progress'))}
                  </div>
                </div>
              )}

              {milestones.filter(m => m.status === 'pending').length > 0 && (
                <div className="milestone-group">
                  <h3 className="group-title pending">
                    <CheckCircle size={18} />
                    Milestones Pending ({milestones.filter(m => m.status === 'pending').length})
                  </h3>
                  <div className="group-list">
                    {renderMilestoneGroup(milestones.filter(m => m.status === 'pending'))}
                  </div>
                </div>
              )}

              {milestones.filter(m => m.status === 'completed').length > 0 && (
                <div className="milestone-group">
                  <h3 className="group-title completed">
                    <CheckCircle size={18} />
                    Milestones Completed ({milestones.filter(m => m.status === 'completed').length})
                  </h3>
                  <div className="group-list">
                    {renderMilestoneGroup(milestones.filter(m => m.status === 'completed'))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showMilestoneForm && (
        <MilestoneForm
          milestone={editingMilestone || initialFormData}
          clients={[]}
          projects={[]}
          onSave={editingMilestone ? handleUpdateMilestone : handleCreateMilestone}
          onCancel={() => {
            setShowMilestoneForm(false);
            setEditingMilestone(null);
          }}
        />
      )}

      {filesModal && (
        <FilesModal
          title={filesModal.title}
          files={filesModal.files}
          onClose={() => setFilesModal(null)}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
